/**
 * HR Chatbot Engine
 *
 * A frontend-only AI chatbot that maps natural language HR queries
 * to the existing API endpoints and returns conversational answers.
 * No external AI API needed — uses intent matching + API data.
 */
import { http } from "./http"
import store from "@/store"

// ─── Intent Definitions ───
const intents = [
    {
        id: "greeting",
        patterns: [
            /^(hi|hello|hey|good\s*(morning|afternoon|evening)|howdy|hola)/i,
        ],
        handler: handleGreeting,
    },
    {
        id: "my_profile",
        patterns: [
            /my\s*(profile|info|details|information)/i,
            /who\s*am\s*i/i,
            /my\s*(name|email|phone|mobile|employee\s*code)/i,
        ],
        handler: handleMyProfile,
    },
    {
        id: "my_salary",
        patterns: [
            /my\s*(salary|ctc|compensation|pay|package|earnings)/i,
            /how\s*much\s*(do\s*i|am\s*i)\s*(earn|make|get\s*paid)/i,
            /what('?s|\s*is)\s*my\s*(salary|ctc|pay)/i,
            /salary\s*[\/&,]\s*ctc/i,
        ],
        handler: handleMySalary,
    },
    {
        id: "leave_balance",
        patterns: [
            /leave\s*(balance|remaining|left|available|count)/i,
            /how\s*many\s*leave/i,
            /my\s*leave/i,
            /remaining\s*leave/i,
        ],
        handler: handleLeaveBalance,
    },
    {
        id: "attendance_today",
        patterns: [
            /^attendance$/i,
            /^attendance\s*(today|status|report)?$/i,
            /my\s*attendance\s*(today|status)?/i,
            /am\s*i\s*(present|marked|checked)/i,
            /today('?s)?\s*attendance/i,
            /check\s*in\s*status/i,
            /who\s*(is|are)\s*(present|absent)/i,
            /present\s*(today|list|members|employees)/i,
            /absent\s*(today|list|members|employees)/i,
        ],
        handler: handleAttendanceToday,
    },
    {
        id: "attendance_summary",
        patterns: [
            /attendance\s*(summary|this\s*month|monthly|history)/i,
            /how\s*many\s*(days|present|absent)/i,
            /my\s*attendance\s*(record|history)/i,
            /monthly\s*attendance/i,
        ],
        handler: handleAttendanceSummary,
    },
    {
        id: "upcoming_holidays",
        patterns: [
            /holiday/i,
            /upcoming\s*holiday/i,
            /next\s*holiday/i,
            /when\s*is\s*(the\s*)?(next\s*)?holiday/i,
            /company\s*holiday/i,
            /day\s*off/i,
        ],
        handler: handleUpcomingHolidays,
    },
    {
        id: "my_manager",
        patterns: [
            /my\s*(manager|supervisor|reporter|reporting|boss)/i,
            /who\s*(do\s*i|i)\s*report\s*to/i,
            /who('?s|\s*is)\s*my\s*(manager|boss|lead)/i,
        ],
        handler: handleMyManager,
    },
    {
        id: "my_department",
        patterns: [
            /my\s*(department|team|division)/i,
            /which\s*department/i,
            /what\s*department/i,
        ],
        handler: handleMyDepartment,
    },
    {
        id: "my_designation",
        patterns: [
            /my\s*(designation|title|position|role|job\s*title)/i,
            /what('?s|\s*is)\s*my\s*(designation|position|role|title)/i,
        ],
        handler: handleMyDesignation,
    },
    {
        id: "upcoming_birthdays",
        patterns: [
            /birthday/i,
            /upcoming\s*birthday/i,
            /who.*birthday/i,
            /bday/i,
            /birth\s*day/i,
            /birthday\s*(today|this\s*week|coming|next|soon)/i,
        ],
        handler: handleUpcomingBirthdays,
    },
    {
        id: "employee_count",
        patterns: [
            /how\s*many\s*employee/i,
            /total\s*employee/i,
            /employee\s*count/i,
            /headcount/i,
            /staff\s*count/i,
        ],
        handler: handleEmployeeCount,
    },
    {
        id: "company_departments",
        patterns: [
            /company\s*[\/&,]\s*department/i,
            /department\s*[\/&,]\s*company/i,
            /org\s*(info|structure|overview)/i,
            /organization/i,
        ],
        handler: handleCompanyDepartments,
    },
    {
        id: "company_info",
        patterns: [
            /^compan(y|ies)$/i,
            /company\s*(info|details|information|list|name|count)/i,
            /how\s*many\s*compan/i,
            /list\s*(of\s*)?compan/i,
            /which\s*compan/i,
            /show\s*compan/i,
            /all\s*compan/i,
        ],
        handler: handleCompanyInfo,
    },
    {
        id: "department_list",
        patterns: [
            /^departments?$/i,
            /list\s*(of\s*)?department/i,
            /all\s*department/i,
            /show\s*department/i,
            /department\s*(list|count|info|details)/i,
            /how\s*many\s*department/i,
        ],
        handler: handleDepartmentList,
    },
    {
        id: "payroll_info",
        patterns: [
            /^payroll$/i,
            /payroll/i,
            /payslip/i,
            /salary\s*slip/i,
            /last\s*(pay|salary)/i,
        ],
        handler: handlePayrollInfo,
    },
    {
        id: "leave_request",
        patterns: [
            /apply\s*(for\s*)?(a\s*)?leave/i,
            /request\s*(a\s*)?leave/i,
            /take\s*(a\s*)?leave/i,
            /want\s*(to\s*)?leave/i,
            /need\s*(a\s*)?day\s*off/i,
        ],
        handler: handleLeaveRequest,
    },
    {
        id: "overtime_info",
        patterns: [
            /overtime/i,
            /extra\s*hours/i,
            /my\s*overtime/i,
        ],
        handler: handleOvertimeInfo,
    },
    {
        id: "asset_info",
        patterns: [
            /^assets?$/i,
            /my\s*asset/i,
            /assigned\s*asset/i,
            /company\s*asset/i,
            /laptop|phone|device/i,
            /asset\s*(allocated|assigned|info|details|overview|status|list|report|count)/i,
            /total\s*asset/i,
            /asset\s*(allocation|return)/i,
        ],
        handler: handleAssetInfo,
    },
    {
        id: "cost_request",
        patterns: [
            /^cost\s*request/i,
            /cost\s*(request|approval)/i,
            /reimbursement\s*(status|request)?/i,
            /expense\s*(request|claim)?/i,
            /submit\s*(an?\s*)?(expense|cost|reimbursement)/i,
        ],
        handler: handleCostRequestInfo,
    },
    {
        id: "project_tasks",
        patterns: [
            /my\s*task/i,
            /assigned\s*task/i,
            /pending\s*task/i,
            /task\s*(list|status)/i,
            /what\s*(are\s*)?my\s*task/i,
        ],
        handler: handleProjectTasks,
    },
    {
        id: "help",
        patterns: [
            /help/i,
            /what\s*can\s*you/i,
            /what\s*do\s*you/i,
            /commands/i,
            /features/i,
            /what\s*should\s*i\s*ask/i,
        ],
        handler: handleHelp,
    },
    {
        id: "thank_you",
        patterns: [
            /thank/i,
            /thanks/i,
            /thx/i,
            /appreciate/i,
        ],
        handler: handleThankYou,
    },
]

// ─── Intent Matcher ───
function matchIntent(input) {
    const trimmed = input.trim()
    for (const intent of intents) {
        for (const pattern of intent.patterns) {
            if (pattern.test(trimmed)) {
                return intent
            }
        }
    }
    return null
}

// ─── Profanity Filter ───
const PROFANITY_LIST = [
    "fuck", "shit", "ass", "bitch", "bastard", "damn", "dick",
    "pussy", "cock", "cunt", "whore", "slut", "fag", "nigger",
    "nigga", "retard", "moron", "idiot", "stupid", "dumb",
    "asshole", "bullshit", "motherfucker", "wtf", "stfu", "lmao",
    "piss", "crap", "douche", "wanker", "twat", "bollocks",
    "arsehole", "tosser", "prick", "jackass", "dipshit",
    "shithead", "dumbass", "fatass", "badass", "horseshit",
    "goddam", "goddamn", "sonofabitch", "sob",
    "chutiya", "madarchod", "behenchod", "bhenchod", "bhosdike",
    "gaand", "gandu", "harami", "haramkhor", "randi", "saala",
    "saale", "kamina", "kamine", "ullu", "gadha",
]

function containsProfanity(input) {
    const normalized = input.toLowerCase().replace(/[^a-z\s]/g, "")
    const words = normalized.split(/\s+/)
    return words.some(word => PROFANITY_LIST.includes(word))
}

const PROFANITY_RESPONSES = [
    "Please keep our conversation professional. I'm here to help with HR queries. 🙏",
    "Let's keep things respectful. How can I assist you with an HR question?",
    "I'd appreciate a professional tone. Feel free to ask about leaves, salary, attendance, or anything HR-related.",
    "That language isn't appropriate for the workplace. I'm happy to help if you rephrase your question.",
]

function profanityResponse() {
    return {
        text: PROFANITY_RESPONSES[Math.floor(Math.random() * PROFANITY_RESPONSES.length)],
        type: "text",
    }
}

// ─── Public API ───
export async function processMessage(input) {
    // Check for profanity before processing
    if (containsProfanity(input)) {
        return profanityResponse()
    }

    const intent = matchIntent(input)
    if (!intent) {
        return fallbackResponse(input)
    }
    try {
        return await intent.handler(input)
    } catch (err) {
        console.error(`Chatbot handler error [${intent.id}]:`, err)
        return {
            text: "Sorry, I had trouble fetching that information. Please try again.",
            type: "error",
        }
    }
}

export function getSuggestions() {
    return [
        { label: "My Profile", query: "Show my profile" },
        { label: "Leave Balance", query: "How many leaves do I have?" },
        { label: "My Salary", query: "What's my salary?" },
        { label: "Holidays", query: "Upcoming holidays" },
        { label: "Birthdays", query: "Upcoming birthdays" },
        { label: "My Tasks", query: "My pending tasks" },
        { label: "Attendance", query: "Attendance" },
        { label: "Help", query: "What can you do?" },
    ]
}

// ─── Handlers ───

function handleGreeting() {
    const user = store.state.auth.user
    const name = user?.firstName || user?.fullName || user?.username || "there"
    const hour = new Date().getHours()
    let greeting = "Hello"
    if (hour < 12) greeting = "Good morning"
    else if (hour < 17) greeting = "Good afternoon"
    else greeting = "Good evening"

    return {
        text: `${greeting}, ${name}! 👋 I'm your HR assistant. Ask me anything about your profile, leaves, salary, attendance, holidays, or tasks.`,
        type: "text",
    }
}

async function handleMyProfile() {
    const user = store.state.auth.user
    if (!user) return { text: "I couldn't find your profile. Please log in again.", type: "error" }

    try {
        const res = await http.get("/auth/me")
        const u = res.data.result
        const fields = [
            { label: "Name", value: u.fullName || `${u.firstName || ""} ${u.lastName || ""}`.trim() || u.username },
            { label: "Email", value: u.email },
            { label: "Phone", value: u.mobile || u.phone || "—" },
            { label: "Employee Code", value: u.employeeCode || "—" },
            { label: "Department", value: u.department?.name || u.departmentName || "—" },
            { label: "Designation", value: u.designation?.name || u.designationName || "—" },
            { label: "Company", value: u.company?.name || u.companyName || "—" },
        ].filter(f => f.value && f.value !== "—" && f.value.trim() !== "")

        return {
            text: "Here's your profile information:",
            type: "profile",
            data: fields,
        }
    } catch {
        return { text: `Your name is **${user.firstName || user.username}** (${user.email}). I couldn't fetch more details right now.`, type: "text" }
    }
}

async function handleMySalary() {
    try {
        const res = await http.get("/auth/me")
        const u = res.data.result
        const ctc = u.ctc || u.salary || u.totalCtc
        if (ctc) {
            return {
                text: `Your CTC is **₹${Number(ctc).toLocaleString("en-IN")}**. For a detailed salary breakdown, check [Payroll History](/payroll-history).`,
                type: "text",
            }
        }
        return {
            text: "Your salary details aren't available here. Please check with HR or visit the [Payroll section](/payroll-history).",
            type: "text",
        }
    } catch {
        return { text: "I couldn't fetch salary details. Please visit the [Payroll History](/payroll-history) page.", type: "text" }
    }
}

async function handleLeaveBalance() {
    try {
        const res = await http.get("/attendance-leave-requests/my-balance")
        const data = res.data.result
        if (data && (Array.isArray(data) ? data.length : true)) {
            if (Array.isArray(data)) {
                const rows = data.map(d => ({
                    label: d.leaveType || d.name || d.type || "Leave",
                    value: `${d.balance ?? d.remaining ?? d.available ?? "—"} / ${d.total ?? d.allocated ?? "—"}`,
                }))
                return {
                    text: "Here's your leave balance:",
                    type: "table",
                    data: rows,
                }
            }
            return { text: `Your leave balance: **${JSON.stringify(data)}**`, type: "text" }
        }
        return { text: "I couldn't find leave balance data. You can check [Leave Requests](/attendance-leave-requests).", type: "text" }
    } catch {
        return {
            text: "Leave balance info isn't available right now. Check the [Leave Requests](/attendance-leave-requests) page for details.",
            type: "text",
        }
    }
}

async function handleAttendanceToday() {
    try {
        const today = new Date().toISOString().slice(0, 10)
        const res = await http.get(`/attendance-records/filter?date=${today}`)
        const records = res.data.result || []

        const getName = (r) => {
            if (r.employee) return r.employee.fullName || `${r.employee.firstName || ""} ${r.employee.lastName || ""}`.trim() || r.employee.username
            if (r.user) return r.user.fullName || `${r.user.firstName || ""} ${r.user.lastName || ""}`.trim() || r.user.username
            return r.employeeName || r.userName || r.fullName || r.name || "Unknown"
        }
        const getStatus = (r) => (r.attendanceStatus?.name || r.status || "").toLowerCase()

        const present = records.filter(r => {
            const s = getStatus(r)
            return s.includes("present") || s.includes("check") || s.includes("working") || s.includes("late") || s.includes("half")
        })
        const absent = records.filter(r => {
            const s = getStatus(r)
            return s.includes("absent")
        })
        const onLeave = records.filter(r => {
            const s = getStatus(r)
            return s.includes("leave")
        })

        const stats = [
            { label: "Total Records", value: records.length },
            { label: "Present", value: present.length },
            { label: "Absent", value: absent.length },
        ]
        if (onLeave.length) stats.push({ label: "On Leave", value: onLeave.length })

        const presentNames = present.slice(0, 10).map(r => getName(r))
        const absentNames = absent.slice(0, 10).map(r => getName(r))

        return {
            text: `Attendance for today (**${new Date().toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}**):`,
            type: "attendance",
            stats,
            present: presentNames,
            absent: absentNames,
            presentTotal: present.length,
            absentTotal: absent.length,
        }
    } catch {
        return { text: "I couldn't fetch today's attendance. Visit [Attendance Records](/attendance-records) for details.", type: "text" }
    }
}

async function handleAttendanceSummary() {
    try {
        const res = await http.get("/misc/dashboard-stats")
        const stats = res.data.result
        if (stats) {
            const fields = []
            if (stats.totalPresent !== undefined) fields.push({ label: "Present", value: stats.totalPresent })
            if (stats.totalAbsent !== undefined) fields.push({ label: "Absent", value: stats.totalAbsent })
            if (stats.totalLeaves !== undefined) fields.push({ label: "On Leave", value: stats.totalLeaves })
            if (stats.totalEmployees !== undefined) fields.push({ label: "Total Employees", value: stats.totalEmployees })
            if (stats.totalOvertimeHours !== undefined) fields.push({ label: "Overtime Hours", value: stats.totalOvertimeHours })

            if (fields.length) {
                return { text: "Attendance summary:", type: "table", data: fields }
            }
        }
        return { text: "Visit [Attendance Records](/attendance-records) for your full attendance history.", type: "text" }
    } catch {
        return { text: "I couldn't load attendance data. Check [Attendance Records](/attendance-records).", type: "text" }
    }
}

async function handleUpcomingHolidays() {
    try {
        const res = await http.get("/attendance-holidays-crud")
        const holidays = res.data.result
        if (holidays && holidays.length) {
            const today = new Date()
            const upcoming = holidays
                .filter(h => new Date(h.date || h.startDate) >= today)
                .sort((a, b) => new Date(a.date || a.startDate) - new Date(b.date || b.startDate))
                .slice(0, 5)

            if (upcoming.length) {
                const rows = upcoming.map(h => ({
                    label: h.name || h.title,
                    value: new Date(h.date || h.startDate).toLocaleDateString("en-IN", {
                        day: "numeric", month: "short", year: "numeric", weekday: "short"
                    }),
                }))
                return { text: "Upcoming holidays:", type: "table", data: rows }
            }
            return { text: "No upcoming holidays found. View all at [Holidays](/attendance-holidays).", type: "text" }
        }
        return { text: "Holiday data isn't available. Check [Holidays](/attendance-holidays).", type: "text" }
    } catch {
        return { text: "I couldn't fetch holiday info. Visit [Holidays](/attendance-holidays).", type: "text" }
    }
}

async function handleMyManager() {
    try {
        const res = await http.get("/auth/me")
        const u = res.data.result
        const mgr = u.supervisor || u.reporter || u.manager || u.reportingTo
        if (mgr) {
            const name = typeof mgr === "string" ? mgr : (mgr.fullName || `${mgr.firstName || ""} ${mgr.lastName || ""}`.trim() || mgr.name)
            return { text: `Your reporting manager is **${name}**${mgr.email ? ` (${mgr.email})` : ""}.`, type: "text" }
        }
        return { text: "Manager information isn't available in your profile. Please check with HR.", type: "text" }
    } catch {
        return { text: "I couldn't fetch your manager details right now.", type: "text" }
    }
}

function handleMyDepartment() {
    const user = store.state.auth.user
    const dept = user?.department?.name || user?.departmentName
    if (dept) {
        return { text: `You belong to the **${dept}** department.`, type: "text" }
    }
    return { text: "Department info isn't available. Check your [Profile](/my-profile).", type: "text" }
}

function handleMyDesignation() {
    const user = store.state.auth.user
    const desig = user?.designation?.name || user?.designationName
    if (desig) {
        return { text: `Your designation is **${desig}**.`, type: "text" }
    }
    return { text: "Designation info isn't available. Check your [Profile](/my-profile).", type: "text" }
}

async function handleUpcomingBirthdays() {
    try {
        const now = new Date()
        const year = now.getFullYear()
        const month = now.getMonth() + 1
        const res = await http.get(
            `/attendance-records/filter?stats=true&date=${year}-${month}-1&search_month=true&search_date=false&limited_fields=true&return_general_leaves=true`
        )
        const data = res.data
        const birthdays = data?.birthdays || []

        if (!birthdays.length) {
            return { text: "No upcoming birthdays found this month. 🎂", type: "text" }
        }

        // Parse dates and find those within next 5 days
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
        const fiveDaysLater = new Date(today)
        fiveDaysLater.setDate(fiveDaysLater.getDate() + 5)

        const upcoming = birthdays
            .map(b => {
                // startDateFormatted is in DD-MM-YYYY (Indian) format
                let bDate
                if (b.startDateFormatted) {
                    const parts = b.startDateFormatted.split("-")
                    if (parts.length === 3) {
                        bDate = new Date(year, parseInt(parts[1], 10) - 1, parseInt(parts[0], 10))
                    }
                }
                if (!bDate && b.dob) {
                    const d = new Date(b.dob)
                    bDate = new Date(year, d.getMonth(), d.getDate())
                }
                if (!bDate && b.date) {
                    const d = new Date(b.date)
                    bDate = new Date(year, d.getMonth(), d.getDate())
                }
                return { name: b.userName || b.name || b.fullName || "Employee", date: bDate }
            })
            .filter(b => b.date && b.date >= today && b.date <= fiveDaysLater)
            .sort((a, b) => a.date - b.date)

        if (!upcoming.length) {
            return { text: "No birthdays in the next 5 days. 🎂", type: "text" }
        }

        const list = upcoming.map(b => {
            const isToday = b.date.toDateString() === today.toDateString()
            const dateStr = isToday
                ? "Today! 🎉"
                : b.date.toLocaleDateString("en-IN", { day: "numeric", month: "short", weekday: "short" })
            return { name: b.name, date: dateStr, isToday }
        })

        return {
            text: `**🎂 Upcoming Birthdays** (next 5 days):`,
            type: "birthdays",
            data: list,
        }
    } catch {
        return { text: "I couldn't fetch birthday info right now. Check the dashboard calendar for birthdays.", type: "text" }
    }
}

async function handleEmployeeCount() {
    try {
        const res = await http.get("/misc/dashboard-stats")
        const stats = res.data.result
        if (stats && stats.totalEmployees !== undefined) {
            return { text: `There are **${stats.totalEmployees} employees** in the organization.`, type: "text" }
        }
        return { text: "Employee count isn't available right now. Check the [Employees](/employees) page.", type: "text" }
    } catch {
        return { text: "I couldn't fetch employee count. Visit [Employees](/employees).", type: "text" }
    }
}

async function handleCompanyInfo() {
    try {
        const res = await http.get("/companies")
        const companies = res.data.result
        if (companies && companies.length) {
            const rows = companies.slice(0, 10).map(c => ({
                label: c.name,
                value: c.city || c.location || c.address || "—",
            }))
            return {
                text: `There are **${companies.length}** companies:`,
                type: "table",
                data: rows,
                actions: [{ label: "View All Companies", url: "/companies" }],
            }
        }
        return { text: "No company data available.", type: "text" }
    } catch {
        return { text: "I couldn't fetch company info. Visit [Companies](/companies).", type: "text" }
    }
}

async function handleDepartmentList() {
    try {
        const res = await http.get("/departments")
        const depts = res.data.result
        if (depts && depts.length) {
            const rows = depts.slice(0, 15).map(d => {
                const count = d.employeeCount ?? d.employees_count ?? d.employeesCount ?? d.totalEmployees ?? d._count?.employees
                return {
                    label: d.name,
                    value: count !== undefined && count !== null ? `${count} employees` : "—",
                }
            })
            return {
                text: `There are **${depts.length}** departments:`,
                type: "table",
                data: rows,
                actions: [{ label: "View All Departments", url: "/departments" }],
            }
        }
        return { text: "No departments found.", type: "text" }
    } catch {
        return { text: "I couldn't fetch department info. Visit [Departments](/departments).", type: "text" }
    }
}

async function handleCompanyDepartments() {
    try {
        const [compRes, deptRes] = await Promise.all([
            http.get("/companies"),
            http.get("/departments"),
        ])
        const companies = compRes.data.result || []
        const depts = deptRes.data.result || []

        const companyRows = companies.slice(0, 8).map(c => ({
            label: c.name,
            value: c.city || c.location || c.address || "—",
        }))
        const deptRows = depts.slice(0, 10).map(d => {
            const count = d.employeeCount ?? d.employees_count ?? d.employeesCount ?? d.totalEmployees ?? d._count?.employees
            return {
                label: d.name,
                value: count !== undefined && count !== null ? `${count} employees` : "—",
            }
        })

        return {
            text: `**${companies.length} Companies** and **${depts.length} Departments** in the organization:`,
            type: "company_departments",
            companies: companyRows,
            departments: deptRows,
            companyCount: companies.length,
            deptCount: depts.length,
        }
    } catch {
        return { text: "I couldn't fetch organization info. Visit [Companies](/companies) or [Departments](/departments).", type: "text" }
    }
}

async function handlePayrollInfo() {
    return {
        text: "For payroll details, visit:\n- [Payroll History](/payroll-history) — View past payslips\n- [Create Payroll](/create-payroll) — Generate new payroll\n- [Salary Histories](/salary-histories) — View salary changes",
        type: "text",
    }
}

function handleLeaveRequest() {
    return {
        text: "To apply for leave, go to [Leave Requests](/attendance-leave-requests) and click **Create**. You can also ask me about your leave balance!",
        type: "text",
        actions: [{ label: "Check Leave Balance", query: "My leave balance" }],
    }
}

async function handleOvertimeInfo() {
    return {
        text: "For overtime details, visit [Overtime Requests](/attendance-overtime-requests). You can view pending, approved, and rejected overtime entries there.",
        type: "text",
    }
}

async function handleAssetInfo() {
    try {
        // Fetch asset stats
        const statsRes = await http.get("/misc/dashboard-stats/assets")
        const data = statsRes.data.result

        const stats = []
        if (data) {
            if (data.totalAssetCount !== undefined) stats.push({ label: "Total Assets", value: data.totalAssetCount })
            if (data.totalAllocatedAssetCount !== undefined) stats.push({ label: "Allocated", value: data.totalAllocatedAssetCount })
            if (data.totalNonAllocatedAssetCount !== undefined) stats.push({ label: "Available", value: data.totalNonAllocatedAssetCount })
        }

        // Fetch recent allocations from last 5 days
        let recentActivity = []
        try {
            const allocRes = await http.get("/asset-allocations")
            const allocations = allocRes.data.result || []
            const fiveDaysAgo = new Date()
            fiveDaysAgo.setDate(fiveDaysAgo.getDate() - 5)

            recentActivity = allocations
                .filter(a => {
                    const d = new Date(a.updatedAt || a.createdAt || a.allocationDate)
                    return d >= fiveDaysAgo
                })
                .slice(0, 6)
                .map(a => {
                    const employee = a.employee?.fullName || a.employee?.firstName || a.employeeName || a.user?.fullName || "Unknown"
                    const asset = a.asset?.name || a.assetName || a.asset?.title || "Asset"
                    const status = a.status?.name || a.statusName || a.allocationStatus?.name || ""
                    const isReturn = status.toLowerCase().includes("return")
                    return {
                        label: `${asset} → ${employee}`,
                        value: isReturn ? "🔄 Returned" : "✅ Allocated",
                        date: new Date(a.updatedAt || a.createdAt || a.allocationDate).toLocaleDateString("en-IN", { day: "numeric", month: "short" }),
                    }
                })
        } catch { /* allocation fetch failed — skip */ }

        if (stats.length) {
            let text = "**Asset Overview:**"
            if (recentActivity.length) {
                text += `\nRecent activity (last 5 days):`
            }

            const tableData = [
                ...stats,
                ...recentActivity.map(a => ({ label: `${a.date} — ${a.label}`, value: a.value }))
            ]

            return {
                text,
                type: "table",
                data: tableData,
                actions: [
                    { label: "View All Assets", url: "/assets" },
                    { label: "Allocations", url: "/asset-allocations" },
                ],
            }
        }

        return { text: "Check [Assets](/assets) for asset details or [Asset Allocations](/asset-allocations) for allocation history.", type: "text" }
    } catch {
        return { text: "I couldn't fetch asset data. Visit [Assets](/assets) for details.", type: "text" }
    }
}

function handleCostRequestInfo() {
    return {
        text: "For cost approvals and reimbursements:\n- [View All Requests](/cost-requests) — See status of all requests\n- [Submit New Request](/cost-requests/create) — File a new cost or reimbursement request",
        type: "text",
    }
}

async function handleProjectTasks() {
    try {
        const res = await http.get("/project-tasks")
        const tasks = res.data.result
        if (tasks && tasks.length) {
            const recent = tasks.slice(0, 5).map(t => ({
                label: t.name,
                value: t.status?.name || t.statusName || "—",
            }))
            return {
                text: `You have **${tasks.length}** tasks. Here are the latest:`,
                type: "table",
                data: recent,
                actions: [{ label: "View All Tasks", url: "/project-tasks" }],
            }
        }
        return { text: "No tasks assigned to you. Check [Project Tasks](/project-tasks).", type: "text" }
    } catch {
        return { text: "I couldn't load your tasks. Visit [Project Tasks](/project-tasks).", type: "text" }
    }
}

function handleHelp() {
    return {
        text: "Here's what I can help you with:",
        type: "help",
        data: [
            { icon: "fas fa-user", label: "My Profile", desc: "Name, email, employee code, department" },
            { icon: "fas fa-indian-rupee-sign", label: "Salary / CTC", desc: "Your compensation details" },
            { icon: "fas fa-calendar-check", label: "Leave Balance", desc: "Remaining leaves by type" },
            { icon: "fas fa-clock", label: "Attendance", desc: "Today's status or monthly summary" },
            { icon: "fas fa-umbrella-beach", label: "Holidays", desc: "Upcoming company holidays" },
            { icon: "fas fa-birthday-cake", label: "Birthdays", desc: "Upcoming birthdays in next 5 days" },
            { icon: "fas fa-user-tie", label: "My Manager", desc: "Who you report to" },
            { icon: "fas fa-building", label: "Company / Departments", desc: "Org info and department list" },
            { icon: "fas fa-tasks", label: "My Tasks", desc: "Project tasks assigned to you" },
            { icon: "fas fa-box", label: "Assets", desc: "Allocated company assets" },
            { icon: "fas fa-receipt", label: "Cost Requests", desc: "Expenses and reimbursements" },
            { icon: "fas fa-money-bill", label: "Payroll", desc: "Payslips and salary history" },
        ],
    }
}

function handleThankYou() {
    const replies = [
        "You're welcome! Let me know if you need anything else. 😊",
        "Happy to help! Feel free to ask anytime.",
        "Anytime! I'm here whenever you need me.",
        "Glad I could help! 👍",
    ]
    return { text: replies[Math.floor(Math.random() * replies.length)], type: "text" }
}

function fallbackResponse(input) {
    return {
        text: `I'm not sure how to help with "${input}". Try asking about your **profile**, **leaves**, **salary**, **attendance**, **holidays**, or **tasks**. Type **help** to see everything I can do.`,
        type: "text",
        actions: [{ label: "Show Help", query: "help" }],
    }
}
